import { MODULE_HEADING } from './constant';
import type {
  SentryCrashLocationInput,
  SentryCrashLocationResult,
  SentryModule,
} from './types';

const areaLabel = (
  module?: SentryModule,
  componentName?: string,
): string => {
  if (module) return MODULE_HEADING[module] ?? module.replace(/_/g, ' ');
  if (componentName) return componentName.replace(/_/g, ' ');
  return 'Dashboard';
};

const boundaryLabel = (input: SentryCrashLocationInput): string => {
  if (input.crashType === 'app_shell_crash') {
    return 'AppErrorBoundary (full-page OOPS)';
  }
  if (input.crashType === 'container_section_crash') {
    const scope =
      input.componentName || input.module || input.feature || 'section';
    return `ContainerErrorBoundary (${scope})`;
  }
  if (input.crashOrigin === 'api') return 'API / network capture';
  if (input.crashOrigin === 'federated') return 'Federated remote module';
  if (input.crashOrigin === 'chunk') return 'Chunk / lazy-load failure';
  if (input.crashOrigin === 'local') return 'Host dashboard (local)';
  if (input.label) return `capture (${input.label})`;
  return 'Unhandled / captureSentryError';
};

/**
 * Build a readable “where did it crash” map for Sentry.
 */
export const buildCrashLocationMap = (
  input: SentryCrashLocationInput,
): SentryCrashLocationResult => {
  const area = areaLabel(input.module, input.componentName);
  const path = input.pathname?.trim() || '(unknown path)';
  const boundary = boundaryLabel(input);

  const steps = [
    'Dashboard (PG)',
    area,
    path,
    ...(input.feature ? [`feature:${input.feature}`] : []),
    boundary,
  ];

  const map = steps.join(' → ');
  const summary = `Crashed in ${area} at ${path}`;

  const context: Record<string, string> = {
    summary,
    map,
    path,
    module: input.module ?? area,
    owning_team: input.owningTeam ?? 'unassigned',
    boundary,
    origin: input.crashOrigin ?? 'unknown',
    priority: input.incidentPriority ?? 'medium',
  };

  if (input.feature) context.feature = input.feature;
  if (input.componentName) context.component = input.componentName;
  if (input.matchedEvent) context.last_merchant_action = input.matchedEvent;
  if (input.apiEndpoint) {
    context.api_endpoint = input.apiEndpoint.split('?')[0];
  }
  if (input.boundaryLevel) context.boundary_level = input.boundaryLevel;

  return { map, summary, context };
};
